import React, { useState, useRef } from 'react';
import { Link as RouterLink, useNavigate, Navigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  Grid,
  Link,
  Checkbox,
  FormControlLabel,
  Divider,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  CircularProgress
} from '@mui/material';
import {
  GitHub as GitHubIcon,
  Google as GoogleIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import EmailVerification from '../components/auth/EmailVerification';
import { useTranslation } from 'react-i18next';
import './AuthPages.css';

const RegisterPage = () => {
  const { t } = useTranslation(['auth', 'common', 'validation']);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    userType: 'tenant',
    termsAccepted: false
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const [registrationSuccessRequiresVerification, setRegistrationSuccessRequiresVerification] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const { register, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const confirmPasswordRef = useRef(null);

  if (authLoading) return null;
  if (isAuthenticated && !registrationSuccessRequiresVerification) return <Navigate to="/" replace />;

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (error) {
        setError('');
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleFirstNameChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, firstName: value }));
    
    let error = '';
    if (!value.trim()) {
      error = t('validation:firstName.required');
    } else if (value.trim().length < 2) {
      error = t('validation:firstName.minLength');
    } else if (value.trim().length > 50) {
      error = t('validation:firstName.maxLength');
    }
    
    setFieldErrors(prev => ({ ...prev, firstName: error }));
    setError('');
  };

  const handleLastNameChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, lastName: value }));
    
    let error = '';
    if (!value.trim()) {
      error = t('validation:lastName.required');
    } else if (value.trim().length < 2) {
      error = t('validation:lastName.minLength');
    } else if (value.trim().length > 50) {
      error = t('validation:lastName.maxLength');
    }
    
    setFieldErrors(prev => ({ ...prev, lastName: error }));
    setError('');
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, email: value }));
    
    let error = '';
    if (!value.trim()) {
      error = t('validation:email.required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      error = t('validation:email.invalid');
    }
    
    setFieldErrors(prev => ({ ...prev, email: error }));
    setError('');
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, password: value }));
    
    let error = '';
    if (!value) {
      error = t('validation:password.required');
    } else if (value.length < 6) {
      error = t('validation:password.minLength');
    }
    
    setFieldErrors(prev => ({ ...prev, password: error }));
    setError('');
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, confirmPassword: value }));
    
    let error = '';
    if (!value) {
      error = t('validation:confirmPassword.required');
    } else if (value !== formData.password) {
      error = t('validation:confirmPassword.mismatch');
    }
    
    setFieldErrors(prev => ({ ...prev, confirmPassword: error }));
    setError('');
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    if (error) {
        setError('');
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const validateStep = () => {
    const newErrors = {};
    let hasError = false;
    
    if (activeStep === 0) {
      if (!formData.firstName.trim()) {
        newErrors.firstName = t('validation:firstName.required');
        hasError = true;
      } else if (formData.firstName.trim().length < 2) {
        newErrors.firstName = t('validation:firstName.minLength');
        hasError = true;
      }
      
      if (!formData.lastName.trim()) {
        newErrors.lastName = t('validation:lastName.required');
        hasError = true;
      } else if (formData.lastName.trim().length < 2) {
        newErrors.lastName = t('validation:lastName.minLength');
        hasError = true;
      }
      
      if (!formData.email.trim()) {
        newErrors.email = t('validation:email.required');
        hasError = true;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = t('validation:email.invalid');
        hasError = true;
      }
    } else if (activeStep === 1) {
      if (!formData.password) {
        newErrors.password = t('validation:password.required');
        hasError = true;
      } else if (formData.password.length < 6) {
        newErrors.password = t('validation:password.minLength');
        hasError = true;
      }
      
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = t('validation:confirmPassword.required');
        hasError = true;
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = t('validation:confirmPassword.mismatch');
        hasError = true;
      }
    } else if (activeStep === 2) {
      if (!formData.termsAccepted) {
        setError(t('validation:terms.required'));
        hasError = true;
      }
    }
    
    setFieldErrors(newErrors);
    return !hasError;
  };

  const steps = [
    t('auth.register.steps.personalInfo'),
    t('auth.register.steps.security'),
    t('auth.register.steps.preferences')
  ];

  const handleStepAction = () => {
    if (!validateStep()) return;

    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      handleNext();
    }
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
  
    setLoading(true);
    setError('');
  
    try {
      const userData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        password: formData.password,
        phone: formData.phone.trim(),
        isTenant: formData.userType === 'tenant',
        isOwner: formData.userType === 'owner'
      };
  
      console.log("Sending registration data:", userData);
      const result = await register(userData);
      console.log("Registration result:", result);
  
      if (result.success && result.requiresVerification) {
        setRegistrationSuccessRequiresVerification(true);
        setRegisteredEmail(result.email || formData.email);
      } else if (result.success) {
         navigate('/dashboard');
      } else {
        console.log("[SHOW ERROR OBJECT RESULT]", result);
        console.log("[SHOW ERROR MESSAGE]", result.error);
        console.log("[SHOW ERROR TYPE]", typeof result.error);
        
        const errorMessage = result.error || t('common.error');
        console.log("[FINAL ERROR MESSAGE]", errorMessage);
        setError(errorMessage);
  
        if (typeof errorMessage === 'string') {
            if (errorMessage.toLowerCase().includes('email')) {
              setActiveStep(0);
            } else if (errorMessage.toLowerCase().includes('mot de passe') || errorMessage.toLowerCase().includes('password')) {
              setActiveStep(1);
            } else if (errorMessage.toLowerCase().includes('téléphone')) {
               setActiveStep(0);
            }
        }
      }
    } catch (err) {
      console.error("Unexpected error during handleSubmit:", err);
      setError(t('auth.register.error.unexpectedError'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSuccess = () => {
    navigate('/dashboard');
  };

  if (registrationSuccessRequiresVerification) {
    return (
      <EmailVerification
        email={registeredEmail}
      />
    );
  }

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <>
            <Box sx={{ mb: 3 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="firstName"
                label={t('common:profile.firstName')}
                name="firstName"
                autoComplete="given-name"
                autoFocus
                inputRef={firstNameRef}
                value={formData.firstName}
                onChange={handleFirstNameChange}
                error={!!fieldErrors.firstName}
                helperText={fieldErrors.firstName}
                disabled={loading}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="lastName"
                label={t('common:profile.lastName')}
                name="lastName"
                autoComplete="family-name"
                inputRef={lastNameRef}
                value={formData.lastName}
                onChange={handleLastNameChange}
                error={!!fieldErrors.lastName}
                helperText={fieldErrors.lastName}
                disabled={loading}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label={t('common:profile.email')}
                name="email"
                autoComplete="email"
                inputRef={emailRef}
                value={formData.email}
                onChange={handleEmailChange}
                error={!!fieldErrors.email}
                helperText={fieldErrors.email}
                disabled={loading}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <TextField
                margin="normal"
                fullWidth
                id="phone"
                label={t('auth.register.phone')}
                name="phone"
                autoComplete="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                disabled={loading}
              />
            </Box>
          </>
        );
      case 1:
        return (
          <>
            <Box sx={{ mb: 3 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label={t('auth.register.password')}
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="new-password"
                inputRef={passwordRef}
                value={formData.password}
                onChange={handlePasswordChange}
                error={!!fieldErrors.password}
                helperText={fieldErrors.password}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label={t('auth.register.togglePasswordVisibility')}
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  ),
                }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label={t('auth.register.confirmPassword')}
                type={showPassword ? 'text' : 'password'}
                id="confirmPassword"
                autoComplete="new-password"
                inputRef={confirmPasswordRef}
                value={formData.confirmPassword}
                onChange={handleConfirmPasswordChange}
                error={!!fieldErrors.confirmPassword}
                helperText={fieldErrors.confirmPassword}
                disabled={loading}
              />
            </Box>
          </>
        );
      case 2:
        return (
          <>
            <FormControl component="fieldset" sx={{ mt: 2, mb: 2 }}>
              <FormLabel component="legend">{t('auth.register.userTypeLabel')}</FormLabel>
              <RadioGroup
                aria-label="user-type"
                name="userType"
                value={formData.userType}
                onChange={(e) => setFormData(prev => ({ ...prev, userType: e.target.value }))}
              >
                <FormControlLabel
                  value="tenant"
                  control={<Radio disabled={loading} />}
                  label={t('common:editProfile.tenantLabel')}
                />
                <FormControlLabel
                  value="owner"
                  control={<Radio disabled={loading} />}
                  label={t('common:editProfile.ownerLabel')}
                />
              </RadioGroup>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  name="termsAccepted"
                  color="primary"
                  checked={formData.termsAccepted}
                  onChange={(e) => setFormData(prev => ({ ...prev, termsAccepted: e.target.checked }))}
                  required
                  disabled={loading}
                />
              }
              label={
                <Typography variant="body2">
                  {t('auth.register.acceptTermsPrefix')}{' '}
                  <Link component={RouterLink} to="/terms" target="_blank" rel="noopener noreferrer">
                    {t('auth.register.termsConditions')}
                  </Link>{' '}
                  {t('auth.register.andText')}{' '}
                  <Link component={RouterLink} to="/privacy" target="_blank" rel="noopener noreferrer">
                    {t('auth.register.privacyPolicy')}
                  </Link>.
                </Typography>
              }
            />
          </>
        );
      default:
        return t('auth.register.unknownStep');
    }
  };

  return (
    <Container component="main" maxWidth="sm" sx={{ py: { xs: 4, md: 8 } }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box className="auth-page-header" sx={{
              width: 'calc(100% + 32px)',
              margin: { xs: '-8px -16px 20px -16px', sm: '-16px -32px 20px -32px' },
              borderRadius: '12px',
              mb: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}>
            <Typography component="h1" variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.3rem', sm: '1.5rem' }, textAlign: 'center', width: '100%' }}>
              {t('auth.register.title')}
            </Typography>

            <Typography variant="body2" sx={{ display: 'block', width: '100%', textAlign: 'center', color: '#ffffff', mt: 1 }}>
              {t('auth.register.alreadyRegistered')} {' '}
              <Link component={RouterLink} to="/login" variant="body2" sx={{ color: '#ffffff', textDecoration: 'underline' }}>
                {t('common:navigation.login')}
              </Link>
            </Typography>
          </Box>
          <Stepper activeStep={activeStep} sx={{ width: '100%', mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Box className="auth-error-alert">
              <Box sx={{ flex: 1 }}>
                <Box>{error}</Box>
                {(error.toLowerCase().includes('email') && error.toLowerCase().includes('déjà')) || 
                 (error.toLowerCase().includes('email') && error.toLowerCase().includes('already')) ||
                 (error.toLowerCase().includes('بريد') && error.toLowerCase().includes('مستخدم')) ? (
                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(255, 255, 255, 0.3)' }}>
                    <Typography variant="body2" sx={{ mb: 0.5, display: 'inline' }}>
                      {t('auth.register.error.alreadyHaveAccount')}{' '}
                      <Link 
                        component={RouterLink} 
                        to="/login" 
                        sx={{ color: '#ffffff', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer', display: 'inline' }}
                      >
                        {t('auth.register.error.loginHere')}
                      </Link>
                    </Typography>
                  </Box>
                ) : null}
              </Box>
              <CloseIcon 
                onClick={() => setError('')} 
                sx={{ cursor: 'pointer', ml: 1 }}
              />
            </Box>
          )}

          <Box component="div" sx={{ width: '100%', minHeight: 250 }}>
            {getStepContent(activeStep)}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 3 }}>
            <Button
              disabled={activeStep === 0 || loading}
              onClick={handleBack}
            >
              {t('common:common.goBack')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleStepAction}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : (activeStep === steps.length - 1 ? t('auth.register.finishRegistration') : t('common:common.next'))}
            </Button>
          </Box>

         
        </Box>
      </Paper>
    </Container>
  );
};

export default RegisterPage;